import { useEffect, useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Building2, Activity, AlertCircle } from 'lucide-react'
import { api } from '@/lib/api'
import { handleApiError } from '@/lib/api-error'
import type { AdminEmpresa, Paginated } from '@/types/admin'

export function Dashboard() {
  const [totalEmpresas, setTotalEmpresas] = useState<number | null>(null)
  const [activas, setActivas] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    async function load() {
      setIsLoading(true)
      try {
        const [all, act] = await Promise.all([
          api.get<Paginated<AdminEmpresa>>('/admin/empresas?per_page=1'),
          api.get<Paginated<AdminEmpresa>>('/admin/empresas?per_page=1&estado=activa'),
        ])
        if (!cancelled) {
          setTotalEmpresas(all.meta?.total ?? 0)
          setActivas(act.meta?.total ?? 0)
        }
      } catch (err) {
        if (!cancelled) handleApiError(err, 'No se pudo cargar el dashboard.')
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground mt-1">Visión general de la plataforma</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Empresas totales</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{isLoading ? '—' : totalEmpresas ?? 0}</div>
            <CardDescription className="mt-1">Empresas registradas en la plataforma</CardDescription>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Suscripciones activas</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{isLoading ? '—' : activas ?? 0}</div>
            <CardDescription className="mt-1">Empresas con suscripción vigente</CardDescription>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Pendientes</CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              {isLoading || totalEmpresas === null || activas === null
                ? '—'
                : Math.max(totalEmpresas - activas, 0)}
            </div>
            <CardDescription className="mt-1">Empresas sin suscripción activa</CardDescription>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
